import {
  type McpUiHostCapabilities,
  type McpUiHostContext,
  type McpUiResourceCsp,
  type McpUiResourceMeta,
  type McpUiResourcePermissions,
  type McpUiDisplayMode,
  type McpUiDownloadFileRequest,
  type McpUiDownloadFileResult,
} from '@modelcontextprotocol/ext-apps/app-bridge';

import { type McpChartFailureStage } from '@/hooks/useAuthWebSocket/mcpChartObservability';
import {
  getAppBundleFetchStatus,
  getCachedAppBundleHtml,
  parseBuildIdFromResourceUri,
} from '@/hooks/useAuthWebSocket/message/appBundle';
import { type THEMES } from '@/providers/Theme/types';

type AppPayload = {
  toolName: string;
  structuredContent: unknown;
};

type McpChartRenderState = {
  renderable: boolean;
  reason?: McpChartFailureStage;
};

export const isChartableStructuredContent = (structuredContent: unknown): boolean => {
  if (typeof structuredContent !== 'object' || structuredContent == null) {
    return false;
  }

  const content = structuredContent as Record<string, unknown>;

  if (content.status !== 'success') {
    return false;
  }

  return typeof content.chartSpec === 'object' && content.chartSpec != null;
};

const getBundleFailureReason = (
  fetchStatus: ReturnType<typeof getAppBundleFetchStatus>,
): McpChartFailureStage => {
  if (fetchStatus === 'not_found') {
    return 'bundle_not_found';
  }

  if (fetchStatus === 'auth_error') {
    return 'bundle_auth_error';
  }

  return 'bundle_fetch_error';
};

export const getMcpChartRenderState = ({
  resourceUri,
  structuredContent,
}: {
  resourceUri: string;
  structuredContent: unknown;
}): McpChartRenderState => {
  if (!isChartableStructuredContent(structuredContent)) {
    return { renderable: false, reason: 'not_chartable' };
  }

  if (!getAppResourceSecurity(resourceUri)) {
    return { renderable: false, reason: 'invalid_resource_uri' };
  }

  const bundleHtml = getCachedAppBundleHtml(resourceUri);

  if (bundleHtml === undefined) {
    return {
      renderable: false,
      reason: getBundleFailureReason(getAppBundleFetchStatus(resourceUri)),
    };
  }

  if (bundleHtml.trim().length === 0) {
    return { renderable: false, reason: 'bundle_empty' };
  }

  return { renderable: true };
};

export const MCP_APP_BUNDLE_SANDBOX = 'allow-scripts allow-forms allow-same-origin';
// The trusted proxy needs a same-origin sandbox so chart apps can create same-origin helper iframes
// during browser-side exports, such as Highcharts' inline style collection.
export const MCP_SANDBOX_PROXY_SANDBOX = 'allow-scripts allow-forms allow-same-origin';

const SQL_RESULTS_CHART_RESOURCE_META: McpUiResourceMeta = {
  csp: {},
  permissions: {},
};

export type AppResourceSecurity = {
  sandbox: string;
  csp?: McpUiResourceCsp;
  permissions?: McpUiResourcePermissions;
};

export const getAppResourceSecurity = (resourceUri: string): AppResourceSecurity | null => {
  if (!parseBuildIdFromResourceUri(resourceUri)) {
    return null;
  }

  return {
    sandbox: MCP_APP_BUNDLE_SANDBOX,
    ...SQL_RESULTS_CHART_RESOURCE_META,
  };
};

export const buildCspHeaderValue = (csp?: McpUiResourceCsp): string => {
  const resourceDomains = csp?.resourceDomains?.join(' ') ?? '';
  const connectDomains = csp?.connectDomains?.join(' ') ?? '';
  const frameDomains = csp?.frameDomains?.join(' ') ?? "'self'";
  const baseUriDomains = csp?.baseUriDomains?.join(' ') ?? "'self'";

  return [
    "default-src 'none'",
    `script-src 'self' 'unsafe-inline' ${resourceDomains}`.trim(),
    `style-src 'self' 'unsafe-inline' ${resourceDomains}`.trim(),
    `connect-src 'self' data: ${connectDomains}`.trim(),
    `img-src 'self' data: ${resourceDomains}`.trim(),
    `font-src 'self' data: ${resourceDomains}`.trim(),
    `media-src 'self' data: ${resourceDomains}`.trim(),
    `frame-src ${frameDomains}`,
    "object-src 'none'",
    `base-uri ${baseUriDomains}`,
  ].join('; ');
};

export const injectCspMetaTag = (html: string, csp?: McpUiResourceCsp): string => {
  const cspHeaderValue = buildCspHeaderValue(csp);
  const cspMeta = `<meta http-equiv="Content-Security-Policy" content="${cspHeaderValue}">`;

  if (/<head\b/i.test(html)) {
    return html.replace(/<head\b[^>]*>/i, (match) => `${match}${cspMeta}`);
  }

  if (/<html\b/i.test(html)) {
    return html.replace(/<html\b[^>]*>/i, (match) => `${match}<head>${cspMeta}</head>`);
  }

  return `<!DOCTYPE html><html><head>${cspMeta}</head><body>${html}</body></html>`;
};

export const MCP_CHART_MAX_HEIGHT = 600;
export const MCP_CHART_MAX_WIDTH = 768;
export const MCP_SUPPORTED_DISPLAY_MODES: McpUiDisplayMode[] = ['inline', 'fullscreen'];

export const getMcpHostCapabilities = (): McpUiHostCapabilities => ({
  logging: {},
  downloadFile: {},
});

const getContainerDimensions = (displayMode: McpUiDisplayMode) => {
  if (displayMode === 'fullscreen') {
    return {
      maxHeight: window.innerHeight,
      maxWidth: window.innerWidth,
    };
  }

  return { maxHeight: MCP_CHART_MAX_HEIGHT, maxWidth: MCP_CHART_MAX_WIDTH };
};

export const createMcpAppHostContext = (
  theme: THEMES,
  displayMode: McpUiDisplayMode = 'inline',
): McpUiHostContext => ({
  theme,
  displayMode,
  availableDisplayModes: MCP_SUPPORTED_DISPLAY_MODES,
  platform: 'web',
  containerDimensions: getContainerDimensions(displayMode),
});

export const resolveMcpDisplayModeRequest = (
  requestedMode: McpUiDisplayMode,
  availableModes: McpUiDisplayMode[],
  currentMode: McpUiDisplayMode,
): McpUiDisplayMode => {
  if (availableModes.includes(requestedMode)) {
    return requestedMode;
  }

  return currentMode;
};

export const isSafeMcpDownloadResourceLinkUri = (uri: string): boolean => {
  try {
    const url = new URL(uri);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const downloadMcpAppFileContents = (
  contents: McpUiDownloadFileRequest['params']['contents'],
): McpUiDownloadFileResult => {
  try {
    for (const item of contents) {
      if (item.type === 'resource') {
        const resource = item.resource;
        const blob =
          'blob' in resource
            ? new Blob(
                [Uint8Array.from(atob(resource.blob), (character) => character.charCodeAt(0))],
                { type: resource.mimeType },
              )
            : new Blob([resource.text], { type: resource.mimeType });

        triggerBlobDownload(blob, resource.uri.split('/').pop() ?? 'download');
        continue;
      }

      if (item.type === 'resource_link') {
        if (!isSafeMcpDownloadResourceLinkUri(item.uri)) {
          return { isError: true };
        }

        window.open(item.uri, '_blank', 'noopener,noreferrer');
      }
    }

    return {};
  } catch {
    return { isError: true };
  }
};

export const applyAppSizeToHostIframe = (
  iframe: HTMLIFrameElement,
  { width, height }: { width?: number; height?: number },
  displayMode: McpUiDisplayMode = 'inline',
): void => {
  if (displayMode === 'fullscreen') {
    iframe.style.height = '';
    iframe.style.minWidth = '';
    return;
  }

  if (height !== undefined) {
    iframe.style.height = `${height}px`;
  }

  if (width !== undefined) {
    iframe.style.minWidth = `min(${width}px, 100%)`;
  }
};

export const createToolResultFromAppPayload = ({ structuredContent }: AppPayload) => ({
  content: [] as [],
  structuredContent: structuredContent as Record<string, unknown>,
});
